import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Zap, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  React.useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/api/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      // Backend always returns 200 (email enumeration protection), but network errors still surface
      toast.error(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="h-8 w-8 grid place-items-center bg-primary text-primary-foreground rounded">
            <Zap className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black tracking-tight text-xl">ContentOS</span>
        </Link>

        {sent ? (
          <div className="rounded-lg border border-border bg-card p-6" data-testid="forgot-success">
            <CheckCircle2 className="h-6 w-6 text-primary mb-3" />
            <h1 className="font-display font-black text-2xl tracking-tight">Check your inbox</h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              If an account with <span className="text-foreground font-semibold">{email}</span> exists,
              we've sent a password reset link. The link is valid for 1 hour.
            </p>
            <p className="mt-4 text-xs text-muted-foreground">
              Didn't get it? Check your spam folder, wait a minute, or{" "}
              <button
                data-testid="link-try-again"
                onClick={() => setSent(false)}
                className="text-primary underline underline-offset-4"
              >
                try again with a different email
              </button>.
            </p>
            <Link
              to="/auth"
              data-testid="link-back-to-signin"
              className="inline-flex items-center gap-1.5 mt-6 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Reset password</div>
            <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tighter leading-tight">
              Forgot your<br />password?
            </h1>
            <p className="mt-4 text-sm text-muted-foreground">
              Enter the email associated with your ContentOS account and we'll send you a reset link.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div>
                <Label className="text-xs uppercase tracking-widest">Email</Label>
                <Input
                  data-testid="input-forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 h-11"
                  placeholder="you@example.com"
                />
              </div>
              <Button
                data-testid="btn-send-reset"
                type="submit"
                disabled={busy || !email.trim()}
                className="w-full h-11"
              >
                {busy ? "Sending…" : "Send reset link"}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>

            <Link
              to="/auth"
              data-testid="link-back-to-signin"
              className="inline-flex items-center gap-1.5 mt-6 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
